import { get, isNumber } from "lodash";

import { ITableColumn, ITableData, ITableRowData } from "../types/table-type";
import { IAggregateFormValue, StatisticsType } from "../types/form-type";
import { IBasicData, Language } from "../types/global-type";

import ConvertTableData from "./ConvertTableData";

import { getOriginColumn, getRealColumn } from "../utils/option_main";
import { getSummaryValue } from "../utils/getAggValue";
import { formatSummaryValue } from "../utils/FormatSummaryValueToJSX";

import { SPLIT_VALUE_BY_AGGREGATE_TYPE } from "../constants/aggregate";
import { convertDateValue } from "../utils/option_secondary";
import { isDateType } from "../utils/tableData_secondary";

interface IProps {
  tableData: ITableData;
  formValue: IAggregateFormValue;
  locale: Language;
}

class CalculationDataBySummary {
  private tableData: ITableData;
  private formValue: IAggregateFormValue;
  private valueColumnId: string;
  private valueColumn?: ITableColumn;
  private isCount: boolean;
  private locale: Language;
  public valueStrIsHTML: boolean = false;

  constructor(props: IProps) {
    const { tableData, formValue: value, locale } = props;
    this.tableData = tableData;
    this.formValue = value;
    this.locale = locale;
    const { calculationTargetId, statisticsType } = value;
    const valueColumn = getOriginColumn(tableData, calculationTargetId);
    if (!!calculationTargetId && !!valueColumn && statisticsType === StatisticsType.STATISTIC_COLUMN) {
      this.valueColumnId = calculationTargetId;
      this.valueColumn = valueColumn;
      this.isCount = false;
    } else {
      this.isCount = true;
      this.valueColumnId = "";
    }
  }

  getValue(): JSX.Element | JSX.Element[] {
    const { filterMeta, calculationTargetId, statisticsType, calculationType } = this.formValue;
    const filteredData = new ConvertTableData({ tableData: this.tableData, filterMeta });
    const getBasicData = this.getDataForRowFunc();
    let originalDataList = filteredData.getBasicDataByFilter(this.tableData, getBasicData, false);
    // 当需要拆分计算时 分3步走
    // 1 拆分拼接字符串
    // 2 拆分数组
    // 3 string 类型的 number 恢复原类型
    // 4 必要时 还原 format 待测试验证
    if (
      statisticsType === StatisticsType.STATISTIC_COLUMN &&
      !!calculationTargetId &&
      SPLIT_VALUE_BY_AGGREGATE_TYPE[calculationType]
    ) {
      if (filteredData.checkIsJoinString(calculationTargetId)) {
        // 基于 ,  拆分
        originalDataList = filteredData.splitJoinString(originalDataList, "cellValue");
      }
      // lookup 拆分数组
      originalDataList = filteredData.splitValueArr(originalDataList, "cellValue");
      // 逆 format 针对 公式类型
      if (filteredData.checkIsFormulaNumber(calculationTargetId)) {
        // string => number
        originalDataList = originalDataList.map((item) => {
          const num = Number.parseFloat(item.cellValue);
          const newValue = {
            cellValue: isNumber(num) && !isNaN(num) ? num : item,
          };
          return newValue;
        });
      }
    }
    /**
     * 时间列 value 兼容旧数据
     * fix https://app.treelab.com.cn/v2/0x1d308/0x15e9eac/rowoyvb89sONmd6
     */
    if (isDateType(getRealColumn(calculationTargetId || "", this.tableData)?.type)) {
      originalDataList = convertDateValue(originalDataList, ["cellValue"]);
    }
    const calcFunc = this.getCalculationFunc();
    const value = calcFunc(originalDataList);
    const valueString = this.formatValue(value);
    return valueString;
  }

  getDataForRowFunc(): (row: ITableRowData) => IBasicData {
    return (row: ITableRowData): { cellValue: any } => {
      const obj = {
        cellValue: this.isCount ? 1 : get(row.cells, this.valueColumnId),
      };
      return obj;
    };
  }

  getCalculationFunc(): (values: IBasicData[]) => string | number {
    if (this.isCount) {
      return (values: IBasicData[]) => values.length;
    }

    const { calculationType } = this.formValue;
    const func = (values: IBasicData[]) => {
      const valueList = values.map((item) => item.cellValue);
      return getSummaryValue(valueList, calculationType);
    };
    return func;
  }

  formatValue(value: number | string): JSX.Element | JSX.Element[] {
    return formatSummaryValue(value, this.formValue, this.tableData, this.locale);
  }
}

export default CalculationDataBySummary;
